import { useState } from 'react';
import produce from 'immer';
import { Theme, css } from '@emotion/react';
import { CaretDown } from '@phosphor-icons/react';
import { priceListPerHour } from 'common/accounting/priceList';
import {
  CoreInstanceTypeTexts,
  CoreInstanceTypeSettings,
  coreInstanceTypeSettings,
  CoreInstanceType,
} from 'common/core/CoreInstanceConstants';
import { DeploymentClientState, useDeploymentClientState } from 'client/state/DeploymentClientState';
import { useDeploymentEnabledState } from 'client/pages/deployment/useDeploymentEnabledState';
import { Button } from 'client/components/Button';
import { Catalog } from 'client/components/Catalog';
import { Help } from 'client/components/Help';
import { Stack } from 'client/components/Stack';
import { Typography } from 'client/components/Typography';
import { borderRadius } from 'client/Theme';
import { formatPrice } from '../deploymentUtils';
import { VMSelectDialog } from './VMSelectDialog';

const catalogStyle = css`
  > li > figure {
    margin-right: 0;
    width: auto !important;
  }
`;

const iconStyle = (theme: Theme) => css`
  width: 56px;
  text-align: center;
  border: 2px solid ${theme.color.grey[60]};
  border-radius: ${borderRadius}px;

  > h5 {
    transform: translateY(2px);
  }
`;

const listStyle = (theme: Theme) => css`
  display: grid;
  grid-template-columns: repeat(3, 80px) 1fr;
  grid-template-rows: repeat(1, 1fr);
  grid-gap: 4px;
  justify-items: left;
  list-style: none;
  margin: 2px 0;

  > li {
    text-align: left;

    > b {
      color: ${theme.color.foreground};
    }

    > span {
      display: block;
    }
  }
`;

const editButtonStyle = (theme: Theme) => css`
  > .icon > svg {
    transform: translate(-2px, 1px);
    color: ${theme.color.grey[100]};
  }
`;

const SizeItemIcon = ({ coreInstanceTypeID }: CoreInstanceTypeSettings) => {
  return (
    <section aria-hidden css={iconStyle}>
      <Typography variant="h5">{CoreInstanceTypeTexts[coreInstanceTypeID]}</Typography>
      {/* <Typography variant="body2">/ hour</Typography> */}
    </section>
  );
};

const SizeItemTitle = ({ coreInstanceTypeID, memoryGiB, vCpu }: CoreInstanceTypeSettings) => {
  const pricePerHour = priceListPerHour.coreInstancePrice[coreInstanceTypeID];

  return (
    <ul css={listStyle}>
      <Typography variant="body2" component="li">
        <b>{formatPrice(pricePerHour)}</b>
        <span>/ hour</span>
      </Typography>
      <Typography variant="body2" component="li">
        <b>{memoryGiB} GB</b>
        <span>Memory</span>
      </Typography>
      <Typography variant="body2" component="li">
        <b>{vCpu}</b>
        <span>vCPU</span>
      </Typography>
    </ul>
  );
};

export const SettingsSize = () => {
  const deploymentID = useDeploymentClientState((state) => state.document?.id || null);
  const coreInstanceTypeID = useDeploymentClientState((state) => state.document?.coreInstanceTypeID || null);
  const coreInstanceType = coreInstanceTypeSettings.find((item) => item.coreInstanceTypeID === coreInstanceTypeID || null);
  const { enabled, isLoading } = useDeploymentEnabledState({ deploymentID });
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const isAllowed = !enabled && !isLoading;

  const handleSave = (coreInstanceTypeID: CoreInstanceType) => {
    setIsDialogOpen(false);

    // Update the document, as we have actually saved coreInstanceTypeID to DB in the dialog
    useDeploymentClientState.setState(
      produce((state: DeploymentClientState) => {
        state.document!.coreInstanceTypeID = coreInstanceTypeID;
      })
    );
  };

  if (!coreInstanceType) return null;

  return (
    <Stack gap={0}>
      <Catalog
        items={[
          {
            id: 'asd',
            icon: <SizeItemIcon {...coreInstanceType} />,
            title: <SizeItemTitle {...coreInstanceType} />,
            action: (
              <Button
                small
                disabled={!isAllowed}
                tooltip={!isAllowed ? 'Please disable deployment first' : undefined}
                tooltipSide="left"
                icon={<CaretDown weight="fill" />}
                css={editButtonStyle}
                onClick={() => setIsDialogOpen(true)}
              >
                Change
              </Button>
            ),
          },
        ]}
        css={catalogStyle}
      />
      <Help>Size can only be changed when the deployment is disabled.</Help>

      <VMSelectDialog
        open={isDialogOpen}
        deploymentID={deploymentID}
        coreInstanceTypeID={coreInstanceTypeID}
        onSave={handleSave}
        onClose={() => setIsDialogOpen(false)}
      />
    </Stack>
  );
};
